import { Component, ContentChild, Input } from '@angular/core';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import type { InnogyComponentRendering } from '@core/jss-models';
import { getIsExperienceEditorActive } from '@core/jss-routing';
import { markAsValidSubmissionAction } from '@innogy/utils-deprecated';
import { Store } from '@ngrx/store';
import type { Observable } from 'rxjs';
import { of, iif } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';
import { NgbCollapse } from '@ng-bootstrap/ng-bootstrap';
import { NgTemplateOutlet, AsyncPipe } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import { TrackStepDirective } from '@analytics/events';

import { ProgressiveFormBodyDirective } from '../progressive-form-body.directive';
import { ProgressiveFormSummaryDirective } from '../progressive-form-summary.directive';
import { ProgressiveFormService } from '../progressive-form.service';
import { ProgressiveFormStepCardComponent } from '../progressive-form-step-card/progressive-form-step-card.component';
import { ProgressiveFormSubmitButtonComponent } from '../progressive-form-submit-button/progressive-form-submit-button.component';

@Component({
  selector:
    'wl-progressive-form-content-step-wrapper[progressiveFormSelectorKey]',
  templateUrl: './progressive-form-content-step-wrapper.component.html',
  standalone: true,
  imports: [
    ProgressiveFormStepCardComponent,
    NgbCollapse,
    TrackStepDirective,
    NgTemplateOutlet,
    ProgressiveFormSubmitButtonComponent,
    AsyncPipe,
    TranslatePipe,
  ],
})
export class ProgressiveFormContentStepWrapperComponent {
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() rendering: InnogyComponentRendering | undefined;
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() title?: string;
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() subtitle?: string;
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() stepNumber?: number;
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() stepName?: string;
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() submitButtonText?: string;
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() submitButtonAutomationId?: string;
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() submitButtonIcon?: IconProp;
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() submitButtonLoading = false;
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() showSubmitButton = true;
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() progressiveFormSelectorKey!: string;
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() formStepId?: string;
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() showWhenFutureStep = true;
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() showWhenSkipped = false;

  @ContentChild(ProgressiveFormBodyDirective)
  formBody?: ProgressiveFormBodyDirective;
  @ContentChild(ProgressiveFormSummaryDirective)
  formSummary?: ProgressiveFormSummaryDirective;

  constructor(
    private readonly store$: Store,
    private readonly progressiveFormService: ProgressiveFormService
  ) {}

  get currentStepIndex$() {
    return this.progressiveFormService.currentStepPresentableNumber$(
      this.progressiveFormSelectorKey,
      this.formStepId
    );
  }

  get totalSteps$() {
    return this.progressiveFormService.totalSteps$(
      this.progressiveFormSelectorKey
    );
  }

  get isSkipped$() {
    return this.progressiveFormService.isSkipped$(
      this.progressiveFormSelectorKey,
      this.formStepId
    );
  }

  get isInExperienceEditor$() {
    return this.store$.select(getIsExperienceEditorActive);
  }

  get isCurrentFormStep$(): Observable<boolean> {
    const isCurrentFormStep$ = this.progressiveFormService
      ?.currentActiveFormStepId$(this.progressiveFormSelectorKey)
      .pipe(map((formId) => this.formStepId === formId));

    return this.isInExperienceEditor$.pipe(
      mergeMap((isInXp) => iif(() => isInXp, of(true), isCurrentFormStep$))
    );
  }

  get isFutureFormStep$() {
    return this.progressiveFormService?.isFutureStep$(
      this.progressiveFormSelectorKey,
      this.formStepId
    );
  }

  get isSubmitted$(): Observable<boolean> {
    return this.progressiveFormService.isPreviousStep$(
      this.progressiveFormSelectorKey,
      this.formStepId
    );
  }

  setEditingFormStep() {
    if (!this.formStepId) {
      return;
    }
    this.store$.dispatch(
      this.progressiveFormService.setActiveFormStep({
        formId: this.progressiveFormSelectorKey,
        stepId: this.formStepId,
        isEditing: true,
      })
    );
  }

  submitForm() {
    if (!this.formStepId) {
      return;
    }
    this.store$.dispatch(
      markAsValidSubmissionAction({
        formId: this.progressiveFormSelectorKey,
        controlId: this.formStepId,
      })
    );
  }
}
